/**
 * The single definition of what an optimistic (not-yet-persisted) CrmObject
 * looks like. Used both by the eager nested-create path (useCreateCrmObject)
 * and by draft chips (draftEntityCapture) so the two can never drift.
 */

import type { CrmObject, PropertyValue } from '@/types/crm';

import type { EntityTypeName } from './types';

export interface OptimisticCrmObjectInput {
  entityId: string;
  entityType: EntityTypeName;
  orgId: number;
  /** Property values as produced by the create form (post cleaning). */
  properties: Record<string, unknown>;
  associations: Array<{
    association_type_name: string;
    target_entity_id: string;
  }>;
}

/**
 * Build a synthetic CrmObject from the properties/associations of a create
 * that has not (yet) been acknowledged by the server, mirroring the store's
 * optimistic entry.
 *
 * `last_event_seq` is 0 on purpose: strictly below the real objectCreated seq
 * (1), so the store's seq guard never skips the authoritative create when it
 * lands (store entry + WS reconcile the real seq).
 */
export function buildOptimisticCrmObject({
  entityId,
  entityType,
  orgId,
  properties,
  associations,
}: OptimisticCrmObjectInput): CrmObject {
  return {
    id: entityId,
    org_id: orgId,
    entity_id: entityId,
    entity_type_name: entityType,
    properties: Object.fromEntries(
      Object.entries(properties).map(([k, v]) => [
        k,
        { value: v } as PropertyValue,
      ])
    ),
    last_event_seq: 0,
    associations: associations.map(a => ({
      association_type_name: a.association_type_name,
      target_entity_id: a.target_entity_id,
    })),
    deleted_at: null,
  };
}
